import { createAsyncThunk } from '@reduxjs/toolkit';
import { getBaseUrl, request } from 'src/helper/api';

const missingIdThunk = (name: string) =>
  createAsyncThunk(name, (_payload: any, thunkAPI) =>
    thunkAPI.rejectWithValue({ actionType: 'missingId', message: 'Missing propertyId' }),
  );

export const getProperties = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/getProperties',
  );

export const getPropertyDetails = (token?: string, propertyId?: string, signal?: AbortSignal) => {
  if (!propertyId) return missingIdThunk('properties/getPropertyDetails');
  return request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/getPropertyDetails',
  );
};

export const updatePropertyAccessInfo = (
  token?: string,
  propertyId?: string,
  signal?: AbortSignal,
  accessMethod?: { label: string; value: string } | null,
) => {
  if (!propertyId) return missingIdThunk('properties/updatePropertyAccessInfo');
  return request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/properties/${propertyId}/actions/updateAccessInformation`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/updatePropertyAccessInfo',
    // accessMethod is passed through so the fulfilled reducer can write the human-readable
    // label/value into detailsSource without needing to look up the access_methods list.
    { propertyId, accessMethod },
  );
};

export const getPropertyResidents = (token?: string, propertyId?: string, signal?: AbortSignal) => {
  if (!propertyId) return missingIdThunk('properties/getPropertyResidents');
  return request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/residents`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/getPropertyResidents',
    { property_uuid: propertyId },
  );
};

export const getPropertyMoreDetails = (
  token?: string,
  propertyId?: string,
  signal?: AbortSignal,
) => {
  if (!propertyId) return missingIdThunk('properties/getPropertyMoreDetails');
  return request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/more-details`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/getPropertyMoreDetails',
    { property_uuid: propertyId },
  );
};

/**
 * Fetches the service requests scoped to a single property.
 *
 * Backed by `GET /api/properties/{property_uuid}/service-requests` (ORI-162).
 * Carries `property_uuid` in the meta payload so reducers can route results to
 * `state.properties.serviceRequestsByProperty[propertyId]` — same pattern as
 * `getPropertyResidents`.
 */
export const getPropertyServiceRequests = (
  token?: string,
  propertyId?: string,
  signal?: AbortSignal,
) => {
  if (!propertyId) return missingIdThunk('properties/getPropertyServiceRequests');
  return request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/service-requests`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/getPropertyServiceRequests',
    { property_uuid: propertyId },
  );
};

/**
 * Fetches the digital access-code change history for a single property.
 *
 * Backed by `GET /api/properties/{property_uuid}/access-code-history`. Offset
 * paginated (`page` / `per_page` query params, `meta.current_page`/`last_page`).
 * Carries `property_uuid` in the meta payload so reducers can route results to
 * `state.properties.accessCodeHistoryByProperty[propertyId]` — same pattern as
 * `getPropertyServiceRequests`.
 */
export const getAccessCodeHistory = (token?: string, propertyId?: string, signal?: AbortSignal) => {
  if (!propertyId) return missingIdThunk('properties/getAccessCodeHistory');
  return request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/access-code-history`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/getAccessCodeHistory',
    { property_uuid: propertyId },
  );
};

export const getMechanicals = (token?: string, propertyId?: string, signal?: AbortSignal) => {
  if (!propertyId) return missingIdThunk('properties/getMechanicals');
  return request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/mechanicals`,
      contentType: 'json',
      token,
      signal,
    },
    'properties/getMechanicals',
    { property_uuid: propertyId },
  );
};

// ── Preferred Vendors (property detail tab) ──────────────────────────────────

// GET api/properties/{propertyId}/preferred-vendors
export const getPreferredVendors = (token?: string, propertyId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/preferred-vendors`,
      contentType: 'json',
      token,
      signal,
    },
    'preferredVendors/getPreferredVendors',
    { propertyId },
  );

export const getCategoryPreferredVendors = (
  token?: string,
  propertyId?: string,
  categoryId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/preferred-vendor/${categoryId}`,
      contentType: 'json',
      token,
      signal,
    },
    'preferredVendors/getCategoryPreferredVendors',
    { propertyId, categoryId },
  );

// GET api/properties/{propertyId}/eligible-vendors
// query: { category_id } — full eligible list for the category, fetched once per category
export const getEligibleVendors = (token?: string, propertyId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/properties/${propertyId}/eligible-vendors`,
      contentType: 'json',
      token,
      signal,
    },
    'preferredVendors/getEligibleVendors',
  );

export const addOverrideCategory = (token?: string, propertyId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/properties/${propertyId}/actions/addOverrideCategory`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'preferredVendors/addOverrideCategory',
    { propertyId },
  );

export const removeOverrideCategory = (token?: string, propertyId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/properties/${propertyId}/actions/removeOverrideCategory`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'preferredVendors/removeOverrideCategory',
    { propertyId },
  );

export const updatePreferredVendor = (token?: string, propertyId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/properties/${propertyId}/actions/updatePreferredVendor`,
      contentType: 'json',
      token,
      signal,
    },
    'preferredVendors/updatePreferredVendor',
    { propertyId },
  );

export const deletePreferredVendor = (token?: string, propertyId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/properties/${propertyId}/actions/deletePreferredVendor`,
      contentType: 'json',
      token,
      signal,
    },
    'preferredVendors/deletePreferredVendor',
    { propertyId },
  );

export const resetToDefaultVendor = (token?: string, propertyId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/properties/${propertyId}/actions/resetToDefaultVendor`,
      contentType: 'json',
      token,
      signal,
    },
    'preferredVendors/resetToDefaultVendor',
    { propertyId },
  );
